/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	type useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation as useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation$data,
	type useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutationVariables as useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation$variables,
	type AddPublicLinkPermissionsInput,
	PublicLinkPermissionsObjectType,
	PublicLinkPermissionsType,
} from './__types__/useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation.ts';
const i18n = defineMessages({
	errorTitle: {
		id: 'public-links.useAddPublicLinkPermissionsMutation.error.title',
		defaultMessage: 'We couldn’t change the public link permission.',
		description:
			'Message shown when there is an error while trying to update the public link permission.',
	},
	errorBody: {
		id: 'public-links.useAddPublicLinkPermissionsMutation.error.body',
		defaultMessage:
			'Open the Share and Restrict dialog to check the current status, or to update the permission.',
		description:
			'Message shown when there is an error while trying to update the public link permission.',
	},
});

export const useAddPublicLinkPermissionsMutation = ({
	objectId,
	objectType = PublicLinkPermissionsObjectType.CONTENT,
	permissions = [PublicLinkPermissionsType.EDIT],
	onCompleted,
	onError,
	flags,
}: {
	objectId: string;
	objectType?: PublicLinkPermissionsObjectType;
	permissions?: PublicLinkPermissionsType[];
	onCompleted: () => void;
	onError: (error) => void;
	flags: FlagsStateContainer;
}) => {
	const { formatMessage } = useIntl();
	const [addPublicLinkPermissionsMutation, { loading }] = useMutation<
		useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation$data,
		useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation$variables
	>(
		graphql`
			mutation useAddPublicLinkPermissionsMutationAddPublicLinkPermissionsMutation(
				$addPublicLinkPermissionsInput: AddPublicLinkPermissionsInput!
			) {
				addPublicLinkPermissions(input: $addPublicLinkPermissionsInput) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			onCompleted: () => {
				onCompleted();
			},
			onError: (error) => {
				void flags.showFlag({
					id: 'updatePublicLinkPermissionsErrorFlagId',
					title: formatMessage(i18n.errorTitle),
					description: formatMessage(i18n.errorBody),
					type: 'error',
					close: 'auto',
				});
				onError(error);
			},
		},
	);

	const addPublicLinkPermissions =
		({ objectId, objectType, permissions }: AddPublicLinkPermissionsInput) =>
		() =>
			addPublicLinkPermissionsMutation({
				variables: {
					addPublicLinkPermissionsInput: {
						objectId,
						objectType,
						permissions,
					},
				},
			});

	return {
		addPublicLinkPermissions: addPublicLinkPermissions({ objectId, objectType, permissions }),
		loading,
	};
};
